import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import JawsScanApi from '../api/jawsScanApi';
import { JawsScanCreate, JawsScanResponse, JawsScanSummaryResponse } from '@/schema/jawsScan';
import { JawsScanUpdate } from '@schema/jawsScan.ts';

// Get scan by ID
export const useJawsScan = (id: string) => {
  return useSuspenseQuery<JawsScanResponse>({
    queryKey: ['jawsScan', id],
    queryFn: () => JawsScanApi.getById(id),
  });
};

// Get scan summary by ID
export const useJawsScanSummary = (id: string, options?: { enabled?: boolean }) => {
  return useQuery<JawsScanSummaryResponse>({
    queryKey: ['jawsScanSummary', id],
    queryFn: () => JawsScanApi.getSummaryById(id),
    enabled: options?.enabled ?? Boolean(id),
  });
};

// Get all scans for a patient
export const useJawsScansByPatient = (patientId: string) => {
  return useQuery<JawsScanResponse[]>({
    queryKey: ['jawsScans', patientId],
    queryFn: () => JawsScanApi.getByPatientId(patientId),
  });
};

// Get scan summaries for a patient
export const useJawsScanSummariesByPatient = (patientId: string) => {
  return useQuery<JawsScanSummaryResponse[]>({
    queryKey: ['jawsScanSummaries', patientId],
    queryFn: () => JawsScanApi.getSummaryByPatientId(patientId),
  });
};

// Create new scan with 3D models
export const useCreateJawsScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, upperJawModel, lowerJawModel }: { data: JawsScanCreate; upperJawModel: Blob; lowerJawModel: Blob }) =>
      JawsScanApi.create(data, lowerJawModel, upperJawModel),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jawsScans'] });
      queryClient.invalidateQueries({ queryKey: ['jawsScanSummaries'] });
    },
  });
};

type DeleteVariables = { scanId: string; patientId: string };

export const useDeleteJawsScan = () => {
  const qc = useQueryClient();

  return useMutation<void, Error, DeleteVariables>({
    mutationFn: ({ scanId }) => JawsScanApi.delete(scanId),
    onSuccess: (_, { scanId, patientId }) => {
      qc.removeQueries({ queryKey: ['jawsScanSummary', scanId] });

      qc.setQueryData<JawsScanSummaryResponse[]>(['jawsScanSummaries', patientId], (old) => old?.filter((item) => item.id !== scanId) ?? []);

      qc.invalidateQueries({ queryKey: ['jawsScans', patientId] });
    },
  });
};

// Update a scan
export const useUpdateJawsScan = (id: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: JawsScanUpdate) => JawsScanApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueryData<JawsScanSummaryResponse>(['jawsScanSummary', id], () => updated);

      qc.setQueryData<JawsScanSummaryResponse[]>(['jawsScanSummaries', updated.patientId], (old) =>
        old ? old.map((item) => (item.id === id ? updated : item)) : [updated],
      );

      qc.setQueryData<JawsScanResponse[]>(['jawsScans', updated.patientId], (old) =>
        old ? old.map((item) => (item.id === id ? { ...item, note: updated.note } : item)) : [],
      );
    },
  });
};
